import React, { useEffect, useMemo, useState } from "react"

import { datadogRum } from "@datadog/browser-rum"
import TagManager from "react-gtm-module"
import {
    Route,
    Routes,
    matchPath,
    useLocation,
    useNavigate,
} from "react-router-dom"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config"
import { IOneVerifyProfile } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { UserIdleTimerElement } from "@front-shared/elements/userIdleTimer.element"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"

import { ConfigurationsComponent } from "../components/configurations/configurations.component"
import { CoverageCheckerComponent } from "../components/coverageChecker/coverageChecker.component"
import { ErrorBoundary } from "../components/general/errorBoundary.component"
import { NotFoundComponent } from "../components/general/notFound.component"
import { OutageBannerComponent } from "../components/general/outageBanner.component"
import { InsightsComponent } from "../components/insights/insights.component"
import { IntegrationsComponent } from "../components/integrations/integrations.component"
import { NetworkStatusRulesComponent } from "../components/networkStatus/networkStatusRules.component"
import { OrganizationComponent } from "../components/organization/organization.component"
import { PayerStatusComponent } from "../components/payerStatus/payerStatus.component"
import { AgenticReportsComponent } from "../components/policies/agentic/agenticReports.component"
import { DownloadExportComponent } from "../components/policies/downloadExport.component"
import { MedicaidReportsComponent } from "../components/policies/medicaidReports/medicaidReports.component"
import { OverridesComponent } from "../components/policies/overrides/overrides.component"
import { PlanYearResetsComponent } from "../components/policies/planYearResets/planYearResets.component"
import { ReportsComponent } from "../components/policies/reports/reports.component"
import { SearchPoliciesComponent } from "../components/policies/search/searchPolicies.component"
import { SinglePolicyAgenticReportsComponent } from "../components/policies/singlePolicy/singlePolicyAgenticReports.component"
import { SinglePolicyAssignedTasksComponent } from "../components/policies/singlePolicy/singlePolicyAssignedTasks.component"
import { SinglePolicyHistoryComponent } from "../components/policies/singlePolicy/singlePolicyHistory.component"
import { SinglePolicyMedicaidReportsComponent } from "../components/policies/singlePolicy/singlePolicyMedicaidReports.component"
import { SinglePolicyOverrideComponent } from "../components/policies/singlePolicy/singlePolicyOverride.component"
import { SinglePolicyPlanYearResetsComponent } from "../components/policies/singlePolicy/singlePolicyPlanYearResets.component"
import { SinglePolicyReportsComponent } from "../components/policies/singlePolicy/singlePolicyReports.component"
import { SinglePolicyTaskAssignmentComponent } from "../components/policies/singlePolicy/singlePolicyTaskAssignment.component"
import { ProfileComponent } from "../components/profile/profile.component"
import { AssignedTasksComponent } from "../components/tasksManagement/assignedTasks.component"
import { BillerGroupsComponent } from "../components/tasksManagement/billerGroups/billerGroups.component"
import { TaskAssignmentsComponent } from "../components/tasksManagement/taskAssignments.component"
import { GENERAL_CONFIG } from "../config/general.config"
import {
    ROUTES_CONFIG,
    ROUTES_FULL_SCREEN_WIDTH,
} from "../config/routes.config"
import { AmplitudeHelper } from "../helpers/amplitude.helper"
import { AuthHelper } from "../helpers/auth.helper"
import { LogoutHelper } from "../helpers/logout.helper"
import { ProduktlyHelper } from "../helpers/produktly.helper"
import { RuntimeHelper } from "../helpers/runtime.helper"
import { SocketsHelper } from "../helpers/sockets.helper"
import { useFeatureFlagsPolling } from "../hooks/featureFlagsPolling.hook"
import { PrivateRoute } from "../router"
import { AuthService } from "../services/auth.service"
import { useAppDispatch } from "../store/appDispatch.hook"
import { useAppSelector } from "../store/selectors/app.selector"
import { runtimeSelector } from "../store/selectors/runtime.selector"
import { userDetailsSelector } from "../store/selectors/userDetails.selector"
import { userDetailsSetSelectedPracticeRole } from "../store/slices/userDetails.slice"
import { practiceConfigurationsGet } from "../store/thunks/practiceConfigurations.thunks"
import { runtimeGetEnabledFeatureFlags } from "../store/thunks/runtime.thunks"
import {
    userDetailsGetPracticeUsageQuota,
    userDetailsGetProfile,
} from "../store/thunks/userDetails.thunks"

export const AppContainer: React.FunctionComponent = () => {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const navigate = useNavigate()

    useFeatureFlagsPolling()

    const userDetailsState = useAppSelector(userDetailsSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const [previousIsLoggedIn, setPreviousIsLoggedIn] = useState<
        boolean | null
    >(false)

    const isIdleTimerDisabled = useMemo(() => {
        return (
            RuntimeHelper.isBulkCoverageChecksLoading() ||
            RuntimeHelper.isPoliciesExportLoading() ||
            RuntimeHelper.isTaskAssignmentsExportProductivityLoading() ||
            RuntimeHelper.isIntegrationsEraUploadLoading()
        )
    }, [runtimeState.isLoading])

    useEffect(() => {
        // Make sure to call this just once
        if (
            runtimeState.isLoggedIn &&
            runtimeState.isLoggedIn !== previousIsLoggedIn &&
            window.location.pathname !== ROUTES_CONFIG.logoutUrl
        ) {
            // Check if need to set MFA
            // If so -> logout user
            AuthService.checkMFASetupNeeded(false).then(needMFASetup => {
                if (needMFASetup) {
                    LogoutHelper.logout(dispatch, undefined, navigate)
                }
            })

            const needTrackLoggedInEvent = BrowserStorageHelper.get(
                GENERAL_CONFIG.browserStorageKeys.needTrackLoggedInEvent,
                undefined,
                BrowserStorageType.sessionStorage,
            )

            const dispatchPromise = dispatch(userDetailsGetProfile())

            if (!needTrackLoggedInEvent) {
                return
            }

            BrowserStorageHelper.remove(
                GENERAL_CONFIG.browserStorageKeys.needTrackLoggedInEvent,
                BrowserStorageType.sessionStorage,
            )

            dispatchPromise.unwrap().then((profile?: IOneVerifyProfile) => {
                if (!profile) {
                    return
                }

                AmplitudeHelper.logEvent(AmplitudeEvent.LOGGED_IN, {
                    userId: profile.id,
                    userPracticeIds: profile.practiceRoles.map(
                        role => role.practice.id,
                    ),
                    userPracticeNames: profile.practiceRoles.map(
                        role => role.practice.name,
                    ),
                    userRoles: profile.practiceRoles.map(role => role.role.key),
                })
            })

            setPreviousIsLoggedIn(runtimeState.isLoggedIn)
        }
    }, [runtimeState.isLoggedIn])

    // Once got user profile - set user id in Google Analytics for better tracking
    useEffect(() => {
        // Set client portal id when logged in, reset when logged out
        TagManager.dataLayer({
            dataLayer: {
                event: "setUserId",
                userId: userDetailsState.profile?.id || null,
            },
        })

        if (userDetailsState.profile) {
            datadogRum.setUser({
                id: userDetailsState.profile.id.toString(),
                email: userDetailsState.profile.email,
            })
        } else {
            datadogRum.clearUser()
        }
    }, [userDetailsState.profile])

    useEffect(() => {
        const { profile, selectedPracticeRole } = userDetailsState

        if (!profile || !selectedPracticeRole) {
            return
        }

        ProduktlyHelper.identifyUser(
            profile.id,
            {
                env: import.meta.env.VITE_ENV,
                practiceId: selectedPracticeRole.practice.id,
                roleKey: selectedPracticeRole.role.key,
                email: AuthHelper.getDemoSession()?.email || profile.email,
                isDemo: AuthHelper.isDemoSessionActive(),
            },
            navigate,
        )
    }, [
        userDetailsState.profile?.id,
        userDetailsState.selectedPracticeRole?.practice.id,
        userDetailsState.selectedPracticeRole?.role.key,
    ])

    // Once got user profile - connect our user to sockets
    // Only happens once user finishes onboarding
    useEffect(() => {
        if (!userDetailsState.profile || !runtimeState.enabledFeatureFlags) {
            return
        }

        const socketsInstance = SocketsHelper.getInstance()

        // Make sure to reconnect only in case it's not connected right now
        if (!socketsInstance.isConnected()) {
            socketsInstance.reconnect(
                dispatch,
                runtimeState.enabledFeatureFlags,
            )
        }
    }, [userDetailsState.profile, runtimeState.enabledFeatureFlags])

    useEffect(() => {
        if (!runtimeState.enabledFeatureFlags) {
            return
        }

        BrowserStorageHelper.set(
            GENERAL_CONFIG.browserStorageKeys.enabledFeatureFlags,
            runtimeState.enabledFeatureFlags,
            BrowserStorageType.sessionStorage,
        )
    }, [runtimeState.enabledFeatureFlags])

    useEffect(() => {
        if (!userDetailsState.profile?.practiceRoles.length) {
            return
        }

        const eagleEyeData = AuthHelper.getEagleEyeViewAsData()

        // By default, once we get profile details and selectedPracticeRole are not set yet
        // Then during page reload check localStorage storage, if not set there, select [0]
        // IF we already have practiceRole set, but we've received profile, update existing practiceRole
        // Local storage so it persists across tabs
        // Give priority to practice selected with Eagle Eye
        const preSelectedPracticeId =
            userDetailsState.selectedPracticeRole?.practice.id ||
            eagleEyeData?.practice.id ||
            BrowserStorageHelper.get(
                GENERAL_CONFIG.browserStorageKeys.selectedPracticeId,
                undefined,
                BrowserStorageType.localStorage,
            )

        const neededPracticeRole = preSelectedPracticeId
            ? userDetailsState.profile.practiceRoles.find(
                  item => item.practice.id === preSelectedPracticeId,
              )
            : undefined

        dispatch(
            userDetailsSetSelectedPracticeRole(
                neededPracticeRole || userDetailsState.profile.practiceRoles[0],
            ),
        )
    }, [userDetailsState.profile])

    // Monitor change of selected practice
    // then fetch enabled feature flags, usage quota and practice configurations
    useEffect(() => {
        if (!userDetailsState.selectedPracticeRole?.practice) {
            return
        }

        const { practice } = userDetailsState.selectedPracticeRole

        dispatch(
            runtimeGetEnabledFeatureFlags({
                payload: practice,
            }),
        )

        dispatch(
            practiceConfigurationsGet({
                payload: practice,
            }),
        )

        dispatch(
            userDetailsGetPracticeUsageQuota({
                payload: practice,
            }),
        )
    }, [userDetailsState.selectedPracticeRole?.practice.id])

    const isFullWidthScreen = useMemo<boolean>(() => {
        return ROUTES_FULL_SCREEN_WIDTH.some(item => {
            return !!matchPath(item, location.pathname)
        })
    }, [location.pathname])

    return (
        <ErrorBoundary userDetails={userDetailsState}>
            <OutageBannerComponent />

            <div
                className={`
                    ${
                        !isFullWidthScreen
                            ? "mr-[45px] ml-[45px] mb-[135px] sm:mr-[16px] sm:ml-[16px]"
                            : ""
                    }
                `}
            >
                <Routes>
                    <Route
                        path={ROUTES_CONFIG.profileUrl}
                        element={
                            <PrivateRoute>
                                <ProfileComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.coverageCheckerUrl}
                        element={
                            <PrivateRoute>
                                <CoverageCheckerComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.searchUrl}
                        element={
                            <PrivateRoute>
                                <SearchPoliciesComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.searchSinglePolicyUrl}
                        element={
                            <PrivateRoute>
                                <SinglePolicyHistoryComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.reportsUrl}
                        element={
                            <PrivateRoute>
                                <ReportsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.reportsSinglePolicyUrl}
                        element={
                            <PrivateRoute>
                                <SinglePolicyReportsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.medicaidReportsUrl}
                        element={
                            <PrivateRoute>
                                <MedicaidReportsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.medicaidReportsSinglePolicyUrl}
                        element={
                            <PrivateRoute>
                                <SinglePolicyMedicaidReportsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.overridesUrl}
                        element={
                            <PrivateRoute>
                                <OverridesComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.overridesSinglePolicyUrl}
                        element={
                            <PrivateRoute>
                                <SinglePolicyOverrideComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.agenticReportsUrl}
                        element={
                            <PrivateRoute>
                                <AgenticReportsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.agenticReportsSinglePolicyUrl}
                        element={
                            <PrivateRoute>
                                <SinglePolicyAgenticReportsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.assignedTasksUrl}
                        element={
                            <PrivateRoute>
                                <AssignedTasksComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.planYearResetsUrl}
                        element={
                            <PrivateRoute>
                                <PlanYearResetsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.planYearResetsSinglePolicyUrl}
                        element={
                            <PrivateRoute>
                                <SinglePolicyPlanYearResetsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.taskAssignmentsUrl}
                        element={
                            <PrivateRoute>
                                <TaskAssignmentsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.taskAssignmentsSinglePolicyUrl}
                        element={
                            <PrivateRoute>
                                <SinglePolicyTaskAssignmentComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.assignedTasksSinglePolicyUrl}
                        element={
                            <PrivateRoute>
                                <SinglePolicyAssignedTasksComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.manageGroupsUrl}
                        element={
                            <PrivateRoute>
                                <BillerGroupsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.organizationUrl}
                        element={
                            <PrivateRoute>
                                <OrganizationComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.configurationsUrl}
                        element={
                            <PrivateRoute>
                                <ConfigurationsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.downloadExportUrl}
                        element={
                            <PrivateRoute>
                                <DownloadExportComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.networkStatusRulesUrl}
                        element={
                            <PrivateRoute>
                                <NetworkStatusRulesComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.insightsUrl}
                        element={
                            <PrivateRoute>
                                <InsightsComponent />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.integrationsUrl}
                        element={
                            <PrivateRoute>
                                <IntegrationsComponent />
                            </PrivateRoute>
                        }
                    />

                    {/*Hidden in sandbox, as menu item, page shows "not found"*/}
                    {!GENERAL_CONFIG.isSandbox && (
                        <Route
                            path={ROUTES_CONFIG.payerStatusUrl}
                            element={
                                <PrivateRoute>
                                    <PayerStatusComponent />
                                </PrivateRoute>
                            }
                        />
                    )}

                    <Route path="*" element={<NotFoundComponent />} />
                </Routes>

                {/*Don't logout if local environment*/}
                {/*Or if bulk csv is running*/}
                {/*Or if exporting list of policies (from reports for example)*/}
                {runtimeState.isLoggedIn &&
                !(GENERAL_CONFIG.isLocal || isIdleTimerDisabled) ? (
                    <UserIdleTimerElement
                        isActive={true}
                        withPrompt
                        idleTimeout={GENERAL_CONFIG.idleTimeoutSeconds}
                        promptBeforeIdleTimeout={59}
                        onIdle={() => {
                            LogoutHelper.logout(dispatch)
                        }}
                    />
                ) : null}
            </div>
        </ErrorBoundary>
    )
}
